import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrandInput, GroundTruthCategory } from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { Button } from '@Components/Button';
import { Design } from '@Components/designConstants';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { BrandProfileFields } from '../components/BrandProfileFields';
import { deleteAdminBrand, getAdminBrand, updateAdminBrand } from './google-ads-audit-admin-api';

const GROUND_TRUTH_CATEGORIES: GroundTruthCategory[] = [
  'conversions',
  'users',
  'changes',
  'settings',
  'audiences',
  'links',
  'performance'
];

interface AdminBrandDetailProps {
  brandId: string;
  onBack: () => void;
  onDeleted: () => void;
}

export const AdminBrandDetail = ({ brandId, onBack, onDeleted }: AdminBrandDetailProps) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BrandInput>({ displayName: '' });
  const [marketsText, setMarketsText] = useState('');
  const [contactsText, setContactsText] = useState('[]');

  const { data } = useQuery({
    queryKey: ['google-ads-audit-admin-brand', brandId],
    queryFn: () => getAdminBrand(brandId)
  });

  useEffect(() => {
    if (!data) return;
    setForm(data.brand);
    setMarketsText((data.brand.markets ?? []).join(', '));
    setContactsText(JSON.stringify(data.brand.clientContacts ?? [], null, 2));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAdminBrand(brandId, {
        ...form,
        markets: marketsText
          .split(',')
          .map(m => m.trim())
          .filter(Boolean),
        clientContacts: JSON.parse(contactsText || '[]')
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['google-ads-audit-admin-brand', brandId] })
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminBrand(brandId),
    onSuccess: onDeleted
  });

  return (
    <div className='google-ads-audit-admin-brand-detail'>
      <div className='google-ads-audit-workspace-header'>
        <InteractiveSpan className='google-ads-audit-back-link' onClick={onBack}>
          ← Brands
        </InteractiveSpan>
        <span className='google-ads-audit-workspace-title'>{data?.brand.displayName}</span>
      </div>
      <BrandProfileFields
        form={form}
        setForm={setForm}
        marketsText={marketsText}
        setMarketsText={setMarketsText}
        contactsText={contactsText}
        setContactsText={setContactsText}
      />
      <div className='google-ads-audit-form-row'>
        <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
          Save
        </Button>
        <Button
          design={Design.NEUTRAL}
          onClick={() => deleteMutation.mutate()}
          loading={deleteMutation.isPending}
        >
          Delete brand
        </Button>
      </div>
      <div className='google-ads-audit-form-section-title'>Ground truths</div>
      <div className='google-ads-audit-admin-ground-truths'>
        {GROUND_TRUTH_CATEGORIES.map(category => {
          const groundTruth = data?.groundTruths.find(g => g.category === category);
          return (
            <div key={category} className='google-ads-audit-admin-ground-truth-card'>
              <span className='google-ads-audit-form-section-title'>{category}</span>
              {groundTruth ? (
                <pre>{JSON.stringify(groundTruth.data, null, 2)}</pre>
              ) : (
                <span className='google-ads-audit-subtitle'>Not yet pulled.</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
